import { type FC, useEffect, useState, useCallback, useRef } from 'react';
import auditApi, { type AuditLogEntry } from '@/api/audit.api';
import snapshotsApi, { type Snapshot } from '@/api/snapshots.api';
import Button from '@/components/ui/Button';

interface HistoryPanelProps {
  canvasId: string;
  isOpen: boolean;
  onClose: () => void;
  onSnapshotRestored?: () => void;
}

type Tab = 'historique' | 'snapshots';

const ACTION_ICONS: Record<string, string> = {
  CREATE: '+',
  UPDATE: '~',
  DELETE: '-',
};

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Creation',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
};

const ENTITY_LABELS: Record<string, string> = {
  Node: 'Noeud',
  Link: 'Lien',
  Canvas: 'Canvas',
  Comment: 'Commentaire',
};

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PAGE_SIZE = 50;

export const HistoryPanel: FC<HistoryPanelProps> = ({
  canvasId,
  isOpen,
  onClose,
  onSnapshotRestored,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('historique');

  // Audit log state
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [hasMoreAudit, setHasMoreAudit] = useState(true);
  const auditScrollRef = useRef<HTMLDivElement>(null);

  // Snapshot state
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const loadAuditEntries = useCallback(
    async (offset = 0) => {
      setAuditLoading(true);
      try {
        const entries = await auditApi.getByCanvas(canvasId, PAGE_SIZE, offset);
        if (offset === 0) {
          setAuditEntries(entries);
        } else {
          setAuditEntries((prev) => [...prev, ...entries]);
        }
        setHasMoreAudit(entries.length === PAGE_SIZE);
        setAuditOffset(offset + entries.length);
      } catch {
        // Silently fail
      } finally {
        setAuditLoading(false);
      }
    },
    [canvasId],
  );

  const loadSnapshots = useCallback(async () => {
    setSnapshotsLoading(true);
    try {
      const data = await snapshotsApi.list(canvasId);
      setSnapshots(data);
    } catch {
      // Silently fail
    } finally {
      setSnapshotsLoading(false);
    }
  }, [canvasId]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === 'historique') {
      setAuditOffset(0);
      setHasMoreAudit(true);
      loadAuditEntries(0);
    } else {
      loadSnapshots();
    }
  }, [isOpen, activeTab, loadAuditEntries, loadSnapshots]);

  const handleAuditScroll = useCallback(() => {
    const el = auditScrollRef.current;
    if (!el || auditLoading || !hasMoreAudit) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      loadAuditEntries(auditOffset);
    }
  }, [auditLoading, hasMoreAudit, auditOffset, loadAuditEntries]);

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const snapshot = await snapshotsApi.create(canvasId);
      setSnapshots((prev) => [snapshot, ...prev]);
    } catch {
      // Silently fail
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleRestore = async (snapshotId: string) => {
    setRestoringId(snapshotId);
    try {
      await snapshotsApi.restore(canvasId, snapshotId);
      onSnapshotRestored?.();
    } catch {
      // Silently fail
    } finally {
      setRestoringId(null);
    }
  };

  const tabStyle = (tab: Tab): React.CSSProperties => ({
    flex: 1,
    padding: '8px 0',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--color-text)' : 'var(--color-text-muted)',
    fontWeight: activeTab === tab ? 600 : 400,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    transition: 'var(--transition-fast)',
  });

  return (
    <div
      className="history-panel"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: 380,
        maxWidth: '100vw',
        height: '100vh',
        backgroundColor: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease-in-out',
        zIndex: 50,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-md) var(--spacing-lg)',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--color-text)',
            margin: 0,
          }}
        >
          Historique
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 18,
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            padding: 4,
          }}
          title="Fermer"
        >
          x
        </button>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}
      >
        <button onClick={() => setActiveTab('historique')} style={tabStyle('historique')}>
          Historique
        </button>
        <button onClick={() => setActiveTab('snapshots')} style={tabStyle('snapshots')}>
          Snapshots
        </button>
      </div>

      {/* Content */}
      {activeTab === 'historique' && (
        <div
          ref={auditScrollRef}
          onScroll={handleAuditScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)',
          }}
        >
          {auditEntries.length === 0 && !auditLoading && (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center',
                padding: 'var(--spacing-xl) 0',
              }}
            >
              Aucune activite enregistree.
            </p>
          )}

          {auditEntries.map((entry) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-sm)',
                padding: 'var(--spacing-sm)',
                backgroundColor: 'var(--color-background)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--radius-md)',
                  backgroundColor:
                    entry.action === 'CREATE'
                      ? 'rgba(34,197,94,0.15)'
                      : entry.action === 'DELETE'
                        ? 'rgba(239,68,68,0.15)'
                        : 'rgba(59,130,246,0.15)',
                  color:
                    entry.action === 'CREATE'
                      ? '#22C55E'
                      : entry.action === 'DELETE'
                        ? '#EF4444'
                        : '#3B82F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {ACTION_ICONS[entry.action] || '?'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text)',
                  }}
                >
                  {ACTION_LABELS[entry.action] || entry.action}{' '}
                  <span style={{ fontWeight: 600 }}>
                    {ENTITY_LABELS[entry.entityType] || entry.entityType}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 2,
                  }}
                >
                  {formatTimestamp(entry.createdAt)}
                </div>
              </div>
            </div>
          ))}

          {auditLoading && (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center',
              }}
            >
              Chargement...
            </p>
          )}
        </div>
      )}

      {activeTab === 'snapshots' && (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-sm)',
          }}
        >
          <Button
            size="sm"
            onClick={handleCreateSnapshot}
            isLoading={creatingSnapshot}
            style={{ alignSelf: 'stretch' }}
          >
            Creer un snapshot
          </Button>

          {snapshotsLoading && (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center',
              }}
            >
              Chargement...
            </p>
          )}

          {!snapshotsLoading && snapshots.length === 0 && (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center',
                padding: 'var(--spacing-xl) 0',
              }}
            >
              Aucun snapshot enregistre.
            </p>
          )}

          {snapshots.map((snapshot) => (
            <div
              key={snapshot.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'var(--color-background)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text)',
                  }}
                >
                  {snapshot.name}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 2,
                  }}
                >
                  {formatTimestamp(snapshot.createdAt)}
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRestore(snapshot.id)}
                isLoading={restoringId === snapshot.id}
                disabled={restoringId !== null}
              >
                Restaurer
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;
